import React, { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { MapPin, Briefcase, ArrowRight, Clock, Building2, Home } from "lucide-react";
import WorkLayout from "@/components/WorkLayout";
import { getPage, LABELS } from "@/data/seoPages";
import { useAuth } from "@/context/AuthContext";
import { isEmployer } from "@/lib/roles";
import api from "@/lib/api";

const money = (j) => {
  const c = j.currency === "EUR" ? "€" : "$";
  const k = (n) => `${c}${Math.round((n || 0) / 1000)}k`;
  return `${k(j.salary_min)}–${k(j.salary_max)}`;
};
const hourly = (j) => (j.hourly_from ? `€${j.hourly_from.toFixed(2)}–€${j.hourly_to.toFixed(2)}/hr` : null);

function JobCard({ j }) {
  return (
    <Link to={`/work/job/${j.id}`} data-testid="seo-job-card" className="w-full text-left block rounded-2xl border border-white/10 bg-slate-900/40 p-5 hover:border-teal-500/40 hover:bg-slate-900/70 transition-colors">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-display font-semibold text-slate-100 truncate">{j.title}</div>
          <div className="text-sm text-slate-400 flex items-center gap-1.5 mt-0.5"><Building2 size={13} /> {j.company}</div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {j.housing && <span data-testid="job-card-housing" className="text-[10px] font-mono uppercase tracking-wide text-indigo-200 bg-indigo-500/10 border border-indigo-500/25 rounded-full px-2 py-0.5 inline-flex items-center gap-1"><Home size={10} /> Housing</span>}
          {j.urgent && <span className="text-[10px] font-mono uppercase tracking-wide text-amber-300 bg-amber-500/10 border border-amber-500/25 rounded-full px-2 py-0.5">Urgent</span>}
        </div>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-400">
        <span className="flex items-center gap-1.5"><MapPin size={13} /> {j.location}</span>
        <span className="flex items-center gap-1.5"><Briefcase size={13} /> {j.job_type}</span>
        <span className="font-mono text-teal-300">{hourly(j) || money(j)}</span>
      </div>
      {j.skills?.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {j.skills.slice(0, 4).map((s) => <span key={s} className="text-[11px] px-2 py-0.5 rounded-full bg-slate-800/80 text-cyan-300 border border-cyan-500/20">{s}</span>)}
        </div>
      )}
      <div className="mt-4 text-sm text-teal-300 font-medium flex items-center gap-1.5">View job &amp; salary <ArrowRight size={15} /></div>
    </Link>
  );
}

export default function WorkLanding() {
  const { slug } = useParams();
  const { user } = useAuth();
  const page = getPage(slug);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  // Once registered/logged in, route into the app instead of asking to register again.
  const appHref = user ? (isEmployer(user.role) ? "/dashboard" : "/jobs") : "/register";
  const ctaLabel = user ? "Browse & apply" : "Create a free profile";
  const [housingOnly, setHousingOnly] = useState(false);

  useEffect(() => {
    if (!page) return;
    setLoading(true);
    const params = { ...page.filter };
    if (housingOnly) params.housing = true;
    api.get("/jobs", { params }).then((r) => setJobs(r.data)).catch(() => {}).finally(() => setLoading(false));
    window.scrollTo(0, 0);
  }, [slug, housingOnly]); // eslint-disable-line

  if (!page) return <Navigate to="/work/jobs" replace />;

  const jsonLd = {
    "@context": "https://schema.org", "@type": "CollectionPage",
    name: page.h1, description: page.metaDescription,
    url: (typeof window !== "undefined" ? window.location.origin : "") + `/work/jobs/${slug}`,
  };

  return (
    <WorkLayout title={page.title} description={page.metaDescription} canonicalPath={`/work/jobs/${slug}`} image={page.hero} jsonLd={jsonLd}>
      <section className="relative">
        <div className="absolute inset-0"><img src={page.hero} alt={page.h1} className="w-full h-full object-cover" /><div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/85 to-slate-950/50" /></div>
        <div className="relative max-w-5xl mx-auto px-5 py-20 sm:py-28">
          <nav className="text-xs text-slate-400 mb-3 flex items-center gap-1.5">
            <Link to="/work/jobs" className="hover:text-teal-300">Jobs</Link> <span>/</span> <span className="text-slate-300">{page.h1}</span>
          </nav>
          <h1 className="font-display font-extrabold text-4xl sm:text-5xl leading-tight">{page.h1}</h1>
          <p className="mt-3 text-slate-300 max-w-2xl">{page.intro[0]}</p>
          <Link to={appHref} data-testid="landing-apply-cta" className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-teal-400 to-cyan-500 text-slate-900 font-semibold">{ctaLabel} <ArrowRight size={17} /></Link>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-5 py-12 grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2">
          <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
            <h2 className="font-display font-bold text-2xl">{loading ? "Loading jobs…" : `${jobs.length} live ${page.h1.toLowerCase().replace("jobs in the netherlands", "jobs")} `}<span className="text-slate-400 text-base font-normal">open now</span></h2>
            {!page.filter.housing && (
              <button onClick={() => setHousingOnly((v) => !v)} data-testid="housing-filter-toggle"
                className={`inline-flex items-center gap-1.5 text-sm rounded-full px-3.5 py-1.5 border transition-colors ${housingOnly ? "bg-indigo-500/15 border-indigo-400/40 text-indigo-200" : "bg-slate-900/50 border-white/10 text-slate-300 hover:border-indigo-400/40"}`}>
                <Home size={14} /> Housing provided {housingOnly ? "✓" : ""}
              </button>
            )}
          </div>
          {!loading && jobs.length === 0 && (
            <div className="rounded-2xl border border-white/10 bg-slate-900/40 p-8 text-center text-slate-400">
              No live openings right now — <Link to={appHref} className="text-teal-300">{user ? "browse all jobs" : "create a profile"}</Link> and we'll alert you the moment one appears.
            </div>
          )}
          <div className="space-y-4">{jobs.map((j) => <JobCard key={j.id} j={j} />)}</div>

          {page.intro.slice(1).map((p, i) => <p key={i} className="mt-6 text-slate-300 leading-relaxed">{p}</p>)}
        </div>

        <aside className="lg:col-span-1">
          <div className="sticky top-24 space-y-6">
            <div className="rounded-2xl border border-white/10 bg-slate-900/40 p-5">
              <div className="font-display font-semibold text-slate-100 mb-3">Related searches</div>
              <ul className="space-y-2">
                {page.related.map((s) => (
                  <li key={s}><Link to={`/work/jobs/${s}`} className="text-sm text-slate-400 hover:text-teal-300 flex items-center gap-1.5"><ArrowRight size={13} /> {LABELS[s]} jobs</Link></li>
                ))}
              </ul>
            </div>
            <div className="rounded-2xl border border-teal-500/20 bg-teal-500/5 p-5">
              <div className="font-display font-semibold text-teal-200 mb-1">New to the Netherlands?</div>
              <p className="text-sm text-slate-300 mb-3">Read our guide on salaries, housing and how to start working in NL.</p>
              <Link to="/work/live-in-netherlands" className="text-sm font-medium text-teal-300 inline-flex items-center gap-1.5">Live &amp; work in NL <ArrowRight size={14} /></Link>
            </div>
          </div>
        </aside>
      </div>
    </WorkLayout>
  );
}
